import type {BoardLabels} from '@github-ui/board-core'

/**
 * A starting set of strings, so a caller does not have to write a whole label set to
 * render a board. Every string either package puts in front of a reader comes from here.
 * The one string `Board.tsx` hard-codes goes to the console rather than to the page, and
 * says at its definition why it is neither overridable nor drawn. The core is handed
 * every string it uses, so a caller with its own wording spreads over these:
 *
 * ```ts
 * labels={{...defaultBoardLabels, board: 'Issues by status'}}
 * ```
 */
export const defaultBoardLabels: BoardLabels = {
  board: 'Board',
  // The swimlane's count goes in its name for the same reason a cell's does: a reader who
  // arrives inside the swimlane never reaches the number drawn beside its heading.
  row: ({rowLabel, totalCount}) =>
    totalCount === undefined ? `${rowLabel} row` : `${rowLabel} row, ${cardCount(totalCount)}`,
  // The column is named before the row: "Done, Alice" reads as one place, where the
  // other order reads as two separate things. On a board whose rows carry no name,
  // there is no row half and a cell is simply its column.
  //
  // The count comes last, after the place, so a reader listening for a cell hears which
  // one it is before hearing how big it is. A cell whose caller stated no count is named
  // with no count at all rather than with a zero, which would be a claim nobody made.
  cell: ({columnLabel, rowLabel, totalCount}) => {
    const place = rowLabel === undefined ? columnLabel : `${columnLabel}, ${rowLabel}`

    return totalCount === undefined ? place : `${place}, ${cardCount(totalCount)}`
  },
  // The unit a bare badge cannot carry. A sighted reader takes "812" from its position
  // beside the heading; there is no position to read when it is announced on its own.
  columnCount: cardCount,
  rowCount: cardCount,
  emptyCell: 'No cards',
  loadingItems: 'Loading cards',
  loadingColumns: 'Loading columns',
  loadingRows: 'Loading rows',
  // Read rather than pressed. The board pages by scrolling, so what a reader needs from
  // these is the fact that something is there, not an instruction to act on.
  loadMoreItems: ({columnLabel, rowLabel}) =>
    rowLabel === undefined ? `More cards in ${columnLabel}` : `More cards in ${columnLabel}, ${rowLabel}`,
  loadMoreColumns: 'More columns',
  loadMoreRows: 'More rows',
  // Advice, not a status. An axis the server stopped enumerating is the one case where
  // scrolling further will not help, so the notice says what will.
  columnsTruncated: 'There are more columns than can be shown. Narrow your search to see the rest.',
  rowsTruncated: 'There are more rows than can be shown. Narrow your search to see the rest.',
}

function cardCount(totalCount: number): string {
  return totalCount === 1 ? '1 card' : `${totalCount} cards`
}
